import { History, Landmark, Lock } from "lucide-react";
import { Link } from "react-router";
import { SectionError } from "~/components/company/section-error";
import { authHeadersFromRequest } from "~/lib/auth-headers.server";
import { changeEventConfig, describeChangeEventDetails } from "~/lib/change-events";
import {
	companyCanonicalUrl,
	companyFromMatches,
	companyNotFoundMeta,
	companySocialMeta,
	fetchCompanySection,
	parseIcoSlug,
} from "~/lib/company-profile";
import { corporateEventConfig } from "~/lib/corporate-events";
import { envConfig } from "~/lib/env-validation";
import { formatDate } from "~/lib/format";
import { generateMetaTags } from "~/lib/seo";
import type { CompanyChangeEvent, CompanyChangesResponse } from "~/types/company";
import type { CorporateEventItem, CorporateEventsResponse } from "~/types/corporate";
import type { Route } from "./+types/historie";

// --- Loader ---

export async function loader({ params, request }: Route.LoaderArgs) {
	const ico = parseIcoSlug(params.icoSlug);
	// Tier-aware endpoint — SSR loaders must recover the bearer token from cookies.
	const headers = await authHeadersFromRequest(request);

	const [changes, events] = await Promise.all([
		fetchCompanySection<CompanyChangesResponse>(
			`${envConfig.apiBaseUrl}/companies/${ico}/changes?limit=200`,
		),
		fetchCompanySection<CorporateEventsResponse>(
			`${envConfig.apiBaseUrl}/companies/${ico}/events?limit=200`,
			headers,
		).catch(() => null),
	]);

	return { changes, events };
}

// --- Meta ---

export function meta({ data: loaderData, matches }: Route.MetaArgs) {
	const company = companyFromMatches(matches);
	if (!company) return companyNotFoundMeta();

	const hasChanges = (loaderData?.changes?.changes.length ?? 0) > 0;
	const hasEvents = (loaderData?.events?.events.length ?? 0) > 0;

	return generateMetaTags({
		...companySocialMeta(company),
		title: `Historie změn – ${company.name} | FirmIndex`,
		description: `Historie změn firmy ${company.name} (IČO: ${company.ico}) — firemní události z listin, změny statutárních orgánů, společníků a vlastnických podílů.`,
		canonical: companyCanonicalUrl(company, "historie"),
		noindex: !hasChanges && !hasEvents,
	});
}

// --- Event presentation (shared config in ~/lib/change-events) ---

function groupByYear(
	events: CompanyChangeEvent[],
): { year: number; events: CompanyChangeEvent[] }[] {
	const map = new Map<number, CompanyChangeEvent[]>();
	for (const event of events) {
		const year = new Date(event.detectedAt).getFullYear();
		const existing = map.get(year);
		if (existing) {
			existing.push(event);
		} else {
			map.set(year, [event]);
		}
	}
	return Array.from(map.entries())
		.sort(([a], [b]) => b - a)
		.map(([year, yearEvents]) => ({ year, events: yearEvents }));
}

/** Czech 3-form pluralization: 1 událost / 2–4 události / 5+ událostí. */
function pluralizeEvents(count: number): string {
	if (count === 1) return "událost";
	if (count >= 2 && count <= 4) return "události";
	return "událostí";
}

// --- Component ---

export default function CompanyHistorieRoute({ loaderData }: Route.ComponentProps) {
	const { changes, events } = loaderData;

	if (!changes && !events) {
		return <SectionError message="Nepodařilo se načíst historii změn." />;
	}

	const changeEvents = changes?.changes ?? [];
	const hasCorporateEvents = (events?.events.length ?? 0) > 0;

	if (changeEvents.length === 0 && !hasCorporateEvents) {
		return (
			<div className="py-8 text-center">
				<History className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
				<p className="text-sm font-medium text-muted-foreground">
					Zatím nebyly zaznamenány žádné změny.
				</p>
				<p className="text-xs text-muted-foreground mt-1">
					Změny v obchodním rejstříku sledujeme průběžně — jakmile nějakou zachytíme, zobrazí se
					zde.
				</p>
			</div>
		);
	}

	const grouped = groupByYear(changeEvents);

	return (
		<div className="flex flex-col gap-6 pt-4">
			{events && hasCorporateEvents && <CorporateEventsSection response={events} />}

			{changeEvents.length > 0 && (
				<div className="card-surface rounded-xl p-6">
					<div className="flex items-center gap-2 mb-4">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
							<History className="h-4 w-4 text-primary-600" />
						</div>
						<h2 className="text-lg font-semibold">Změny v rejstříku</h2>
						<span className="text-xs text-muted-foreground ml-auto">
							Celkem {changeEvents.length}{" "}
							{changeEvents.length === 1 ? "změna" : changeEvents.length < 5 ? "změny" : "změn"}
						</span>
					</div>

					<div className="flex flex-col gap-6">
						{grouped.map((group) => (
							<div key={group.year}>
								<h3 className="text-sm font-semibold text-muted-foreground mb-2 border-b border-gray-100 pb-1">
									{group.year}
								</h3>
								<div className="flex flex-col gap-2">
									{group.events.map((event) => (
										<ChangeEventRow key={event.id} event={event} />
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

// --- Corporate events (extracted from Sbírka listin documents) ---

function CorporateEventsSection({ response }: { response: CorporateEventsResponse }) {
	const { events, total, truncated, viewerTier } = response;
	const hiddenCount = Math.max(0, total - events.length);
	const showUpsell = truncated && (viewerTier === "anon" || viewerTier === "free");

	return (
		<div className="card-surface rounded-xl p-6">
			<div className="flex items-center gap-2 mb-4">
				<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50">
					<Landmark className="h-4 w-4 text-primary-600" />
				</div>
				<h2 className="text-lg font-semibold">Firemní události</h2>
				<span className="text-xs text-muted-foreground ml-auto">
					Celkem {total} {pluralizeEvents(total)}
				</span>
			</div>

			<div className="flex flex-col gap-2">
				{events.map((event) => (
					<CorporateEventRow key={event.id} event={event} />
				))}
			</div>

			{showUpsell && (
				<div className="mt-4 rounded-lg border border-dashed border-primary-200 bg-primary-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
					<div className="flex items-start gap-3">
						<Lock className="h-4 w-4 text-primary-600 mt-0.5 shrink-0" />
						<div className="flex flex-col">
							<p className="text-sm font-medium text-foreground">
								{viewerTier === "anon"
									? `${hiddenCount < 5 ? "Další" : "Dalších"} ${hiddenCount} ${pluralizeEvents(hiddenCount)} po registraci zdarma`
									: `${hiddenCount < 5 ? "Další" : "Dalších"} ${hiddenCount} ${pluralizeEvents(hiddenCount)} v plánu Premium`}
							</p>
							<p className="text-xs text-muted-foreground mt-0.5">
								{viewerTier === "anon"
									? "Po registraci uvidíte více událostí včetně popisu. Premium odemkne celou historii i detaily."
									: "Premium plán odemkne celou historii firemních událostí včetně detailů."}
							</p>
						</div>
					</div>
					<Link
						to={viewerTier === "anon" ? "/login?mode=register" : "/cenik"}
						className="btn btn-sm btn-primary shrink-0"
					>
						{viewerTier === "anon" ? "Zaregistrujte se zdarma" : "Upgradovat"}
					</Link>
				</div>
			)}
		</div>
	);
}

function CorporateEventRow({ event }: { event: CorporateEventItem }) {
	const config = corporateEventConfig(event.eventType);
	const Icon = config.icon;

	return (
		<div className="flex items-start gap-3 rounded-md border border-gray-100 px-3 py-2.5">
			<div
				className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.iconClasses}`}
			>
				<Icon className="h-4 w-4" />
			</div>
			<div className="flex flex-col gap-0.5 min-w-0 flex-1">
				<p className="text-sm">
					<span className="font-medium">{config.label}</span>
					{event.title && event.title !== config.label && (
						<span className="text-muted-foreground"> — {event.title}</span>
					)}
				</p>
				{event.description && <p className="text-xs text-muted-foreground">{event.description}</p>}
			</div>
			<span className="text-xs text-muted-foreground whitespace-nowrap mt-0.5">
				{formatDate(event.eventDate)}
			</span>
		</div>
	);
}

function ChangeEventRow({ event }: { event: CompanyChangeEvent }) {
	const config = changeEventConfig(event.eventType);
	const Icon = config.icon;
	const detail = describeChangeEventDetails(event);

	return (
		<div className="flex items-start gap-3 rounded-md border border-gray-100 px-3 py-2.5">
			<div
				className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.iconClasses}`}
			>
				<Icon className="h-4 w-4" />
			</div>
			<div className="flex flex-col gap-0.5 min-w-0 flex-1">
				<p className="text-sm font-medium">{config.label}</p>
				{detail && <p className="text-xs text-muted-foreground">{detail}</p>}
			</div>
			<span className="text-xs text-muted-foreground whitespace-nowrap mt-0.5">
				{formatDate(event.detectedAt)}
			</span>
		</div>
	);
}
